import { DailyNutrition } from '@/types/nutrition'
import {
  MultiDayParseResult,
  MultiDayParseSuccess,
  MultiDayParseFailure,
  ParserConfig,
  defaultParserConfig,
  NutritionParseError,
} from '@/types/parser'
import { NutritionDataParser } from './NutritionDataParser'
import { MarkdownTableParser } from './MarkdownTableParser'

/**
 * Parser for handling multi-day nutrition data files.
 *
 * Handles data files that contain multiple days of nutrition data,
 * splitting them by date headers and parsing each day independently.
 *
 * @example
 * ```typescript
 * const parser = new MultiDayNutritionParser()
 * const result = parser.parseMultiDay(multiDayData)
 *
 * if (result.success) {
 *   console.log(`Parsed ${result.data.length} days`)
 *   result.data.forEach(day => {
 *     console.log(`${day.date.toDateString()}: ${day.totals.calories} calories`)
 *   })
 * }
 * ```
 */
export class MultiDayNutritionParser {
  private singleDayParser = new NutritionDataParser()

  /**
   * Parses multi-day nutrition data string into array of structured data.
   *
   * @param rawData - Raw nutrition data containing multiple days
   * @param config - Optional configuration overrides for parsing behavior
   * @returns MultiDayParseResult containing either successful data array or failure with error
   */
  parseMultiDay(rawData: string, config?: Partial<ParserConfig>): MultiDayParseResult {
    const mergedConfig: ParserConfig = { ...defaultParserConfig, ...config }
    const warnings: string[] = []
    const parsedDays: DailyNutrition[] = []

    try {
      if (!rawData || rawData.trim() === '') {
        throw new NutritionParseError('Empty input data')
      }

      // Convert Markdown table format to space-separated format if necessary
      let processedData = rawData
      if (MarkdownTableParser.isMarkdownFormat(rawData)) {
        const markdownParser = new MarkdownTableParser()
        processedData = markdownParser.convertToSpaceSeparated(rawData)
        warnings.push('Converted from Markdown table format')
      }

      // Split data by days
      const dayDataSections = this.splitByDays(processedData)

      if (dayDataSections.length === 0) {
        throw new NutritionParseError('No valid day sections found in data')
      }

      // Parse each day
      for (const [index, dayData] of dayDataSections.entries()) {
        try {
          const result = this.singleDayParser.parse(dayData, mergedConfig)

          if (result.success) {
            parsedDays.push(result.data)
            warnings.push(...result.warnings)
          } else {
            if (mergedConfig.strictMode) {
              // In strict mode, fail the entire parsing if any day fails
              const error = new NutritionParseError(
                `Failed to parse day ${index + 1}: ${result.error.message}`
              )
              const multiDayFailure: MultiDayParseFailure = {
                success: false,
                error,
                partialData: parsedDays,
              }
              return multiDayFailure
            } else {
              // In non-strict mode, skip failed days and continue
              warnings.push(`Skipped day ${index + 1}: ${result.error.message}`)
            }
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error'

          if (mergedConfig.strictMode) {
            const parseError = new NutritionParseError(
              `Failed to parse day ${index + 1}: ${errorMessage}`
            )
            const multiDayFailure: MultiDayParseFailure = {
              success: false,
              error: parseError,
              partialData: parsedDays,
            }
            return multiDayFailure
          } else {
            warnings.push(`Skipped day ${index + 1}: ${errorMessage}`)
          }
        }
      }

      if (parsedDays.length === 0) {
        throw new NutritionParseError('No valid days could be parsed from the input data')
      }

      // Sort by date (newest first)
      parsedDays.sort((a, b) => b.date.getTime() - a.date.getTime())

      const multiDaySuccess: MultiDayParseSuccess = {
        success: true,
        data: parsedDays,
        warnings,
      }

      return multiDaySuccess
    } catch (error) {
      const parseError =
        error instanceof NutritionParseError
          ? error
          : new NutritionParseError(
              error instanceof Error ? error.message : 'Unknown parsing error'
            )

      const multiDayFailure: MultiDayParseFailure = {
        success: false,
        error: parseError,
        partialData: parsedDays,
      }

      return multiDayFailure
    }
  }

  /**
   * Splits raw multi-day data into individual day sections.
   *
   * @param rawData - Raw multi-day nutrition data
   * @returns Array of individual day data strings
   */
  private splitByDays(rawData: string): string[] {
    const lines = rawData.split('\n')
    const dayDataSections: string[] = []
    let currentDayLines: string[] = []

    // Date pattern to match lines like "Sep 1, 2025" or "January 15, 2024"
    const datePattern = /^[A-Za-z]{3}\s+\d{1,2},\s+\d{4}$|^[A-Za-z]+\s+\d{1,2},\s+\d{4}$/

    for (const line of lines) {
      const trimmedLine = line.trim()

      // Check if this line is a date header
      if (datePattern.test(trimmedLine)) {
        // If we have accumulated lines for a previous day, save them only if they start with a valid date
        if (currentDayLines.length > 0) {
          const firstLine = currentDayLines[0]?.trim() || ''
          if (datePattern.test(firstLine)) {
            dayDataSections.push(currentDayLines.join('\n'))
          }
        }

        // Start new day section with the date line
        currentDayLines = [trimmedLine]
      } else {
        // Only add to current section if we have a valid date header
        if (currentDayLines.length > 0) {
          currentDayLines.push(line)
        }
        // If we don't have a valid date header, ignore this line
      }
    }

    // Don't forget the last day section (if it has a valid date header)
    if (currentDayLines.length > 0) {
      const firstLine = currentDayLines[0]?.trim() || ''
      if (datePattern.test(firstLine)) {
        dayDataSections.push(currentDayLines.join('\n'))
      }
    }

    return dayDataSections.filter((section) => {
      const trimmed = section.trim()
      return trimmed.length > 0 && datePattern.test(trimmed.split('\n')[0] || '')
    })
  }

  /**
   * Detects if input data contains multiple days.
   *
   * @param data - Raw nutrition data
   * @returns True if data contains multiple date headers
   */
  static isMultiDay(data: string): boolean {
    const datePattern = /^[A-Za-z]{3}\s+\d{1,2},\s+\d{4}$|^[A-Za-z]+\s+\d{1,2},\s+\d{4}$/
    const lines = data.split('\n').map((line) => line.trim())

    let dateCount = 0
    for (const line of lines) {
      if (datePattern.test(line)) {
        dateCount++
        if (dateCount > 1) {
          return true
        }
      }
    }

    return false
  }
}
